import {tierColor as passTierColor} from '../utils/passTiers.js';
import { useTonight } from '../hooks/useTonight.js';
import { usableVerdict } from '../utils/observations.js';
import { useState, useEffect } from 'react';
import BottomSheet from './BottomSheet.jsx';
import PopoutModal from './PopoutModal.jsx';
import { useIsMobile } from '../hooks/useIsMobile.js';
import { PASS_WINDOW_HOURS } from '../utils/passTiers.js';

const API = import.meta.env.VITE_API_BASE || '/api';

const MODES = [['sat','Satellites'], ['planet','Planets'], ['deepsky','Deep-sky']];

const TIER_LABEL = ['Below horizon', 'Naked-eye', 'Worth a look', 'Photograph'];
const TIER_ICON  = ['', 'ti-eye', 'ti-star', 'ti-camera'];
const EXPERIENCE = [['casual','Eyes'], ['beginner','Camera'], ['advanced','Pro']];

const COMPASS = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
const compass = d => d == null ? '' : COMPASS[Math.round(((d % 360) / 22.5)) % 16];
const fmtTime = iso => new Date(iso).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' });
function countdown(iso) {
  const m = Math.floor((new Date(iso) - Date.now()) / 60000);
  if (m < 0) return 'now';
  return m > 60 ? `in ${Math.floor(m/60)}h ${m%60}m` : `in ${m}m`;
}

// score 0..100 → color band for planets/deep-sky
function scoreColor(s) {
  if (s >= 70) return 'var(--amber)';
  if (s >= 45) return 'var(--green)';
  if (s >= 20) return 'var(--cyan)';
  return 'var(--txt-mute)';
}

function Briefing({ pass, experience }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let c = false;
    setLoading(true); setError(null); setData(null);
    fetch(`${API}/ai/pass-briefing`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pass, experience }),
    })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`briefing ${r.status}`)))
      .then(d => { if (!c) { setData(d); setLoading(false); } })
      .catch(e => { if (!c) { setError(e.message); setLoading(false); } });
    return () => { c = true; };
  }, [pass.norad_id, pass.start, experience]);
  if (loading) return <div style={{ padding:'8px 0', color:'var(--txt-dim)', fontSize: 'var(--ui-label)' }}>Writing briefing&hellip;</div>;
  if (error)   return <div style={{ padding:'8px 0', color:'var(--rose)', fontSize: 'var(--ui-label)' }}>Briefing unavailable.</div>;
  if (!data) return null;
  const Row = ({ icon, text }) => text ? (
    <div style={{ display:'flex', gap:8, marginBottom:6, alignItems:'flex-start' }}>
      <i className={`ti ${icon}`} style={{ fontSize: 'var(--ui-body)', color:'var(--accent)', marginTop:1, flexShrink:0 }} />
      <span style={{ fontSize: 'var(--ui-label)', lineHeight:1.5, color:'var(--txt)' }}>{text}</span>
    </div>
  ) : null;
  return (
    <div style={{ padding:'10px 0 4px' }}>
      {data.headline && <div style={{ fontFamily:'var(--serif)', fontStyle:'italic', fontSize: 'var(--ui-body)', color:'var(--txt-bright)', lineHeight:1.4, marginBottom:8 }}>{data.headline}</div>}
      <Row icon="ti-compass" text={data.where_to_look} />
      <Row icon="ti-clock" text={data.timing} />
      <Row icon="ti-adjustments" text={data.settings} />
      <Row icon="ti-bulb" text={data.tip} />
    </div>
  );
}

const ACTIVITIES = [
  ['iss','ISS pass','sat'],
  ['deepsky','Deep-sky','deepsky'],
  ['lunar','Lunar','planet'],
  ['aurora','Aurora','sat'],
];
const VERDICT_COLOR = { Excellent:'var(--amber)', Good:'var(--green)', Marginal:'var(--cyan)', Poor:'var(--txt-mute)' };
const FACTOR_COLOR = { good:'var(--green)', mid:'var(--cyan)', bad:'var(--rose)' };

function VerdictHeader({ observer, activity, onActivity }) {
  const { verdict: v, loading } = useTonight(true, observer, activity);

  const score = usableVerdict(v) ? v.score : null;
  const color = VERDICT_COLOR[v?.verdict] || 'var(--txt-mute)';
  const circ = 2 * Math.PI * 26;
  const off = circ * (1 - (score ?? 0) / 100);

  return (
    <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--hair)', flexShrink:0 }}>
      {/* activity selector */}
      <div style={{ display:'flex', gap:4, marginBottom:12 }}>
        {ACTIVITIES.map(([val, label]) => (
          <button key={val} onClick={() => onActivity(val)} style={{
            flex:1, padding:'5px 0', fontFamily:'var(--mono)', fontSize: 'var(--ui-label)', letterSpacing:'0.03em',
            border:'1px solid', borderColor: activity===val ? 'var(--accent)' : 'var(--hair-2)',
            borderRadius:4, cursor:'pointer',
            background: activity===val ? 'var(--bg-2)' : 'transparent',
            color: activity===val ? 'var(--txt-bright)' : 'var(--txt-dim)',
          }}>{label}</button>
        ))}
      </div>
      {/* score ring + verdict */}
      <div style={{ display:'flex', gap:14, alignItems:'center' }}>
        <div style={{ position:'relative', width:64, height:64, flexShrink:0 }}>
          <svg width="64" height="64" style={{ transform:'rotate(-90deg)' }}>
            <circle cx="32" cy="32" r="26" fill="none" stroke="var(--hair-2)" strokeWidth="5" />
            <circle cx="32" cy="32" r="26" fill="none" stroke={color} strokeWidth="5" strokeLinecap="round"
              strokeDasharray={circ} strokeDashoffset={off} style={{ transition:'stroke-dashoffset .5s, stroke .5s' }} />
          </svg>
          <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
            <span style={{ fontSize:18, fontWeight:500, color:'var(--txt-bright)', fontFamily:'var(--mono)', lineHeight:1 }}>{loading ? '\u2026' : (score ?? '-')}</span>
          </div>
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontFamily:'var(--serif)', fontStyle:'italic', fontSize:15, color, marginBottom:2 }}>
            {loading ? 'Reading the sky\u2026' : (v?.verdict || 'No data')}
          </div>
          <div style={{ fontFamily:'var(--mono)', fontSize: 'var(--ui-label)', color:'var(--txt-dim)', lineHeight:1.4 }}>
            {v?.activity_label} tonight
          </div>
        </div>
      </div>
      {/* factor chips */}
      {v?.factors?.length > 0 && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginTop:10 }}>
          {v.factors.map((f, i) => (
            <span key={i} style={{ fontFamily:'var(--mono)', fontSize: 'var(--ui-small)', padding:'2px 7px', borderRadius:99,
              background:'var(--bg-2)', color: FACTOR_COLOR[f.valence] || 'var(--txt-dim)',
              border:`1px solid ${FACTOR_COLOR[f.valence] || 'var(--hair-2)'}` }}>
              {f.key} {f.value}
            </span>
          ))}
        </div>
      )}
      {v?.conditions?.light_pollution_source === 'blackmarble_estimate' && (activity === 'deepsky') && (
        <div style={{ fontFamily:'var(--mono)', fontSize: 'var(--ui-small)', color:'var(--txt-mute)', marginTop:8, lineHeight:1.4 }}>
          Bortle est. from VIIRS radiance (NASA Black Marble) &middot; not a ground measurement
        </div>
      )}
    </div>
  );
}

export default function SkyPlanner({ observer, open, onClose, onGoto }) {
  const isMobile = useIsMobile();
  const [mode, setMode] = useState('sat');
  const [activity, setActivity] = useState('deepsky');
  function handleActivity(a) {
    setActivity(a);
    const m = ACTIVITIES.find(x => x[0] === a)?.[2];
    if (m) setMode(m);
  }
  const [items, setItems] = useState([]);
  const [conditions, setConditions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [experience, setExperience] = useState('beginner');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!open || !observer) return;
    setLoading(true); setError(null); setItems([]); setConditions(null); setExpanded(null);
    const { lat, lon } = observer;
    let url;
    if (mode === 'sat')     url = `${API}/passes/tonight?lat=${lat}&lon=${lon}&hours=${PASS_WINDOW_HOURS}`;
    else if (mode === 'planet') url = `${API}/sky?lat=${lat}&lon=${lon}`;
    else                    url = `${API}/deepsky?lat=${lat}&lon=${lon}`;

    fetch(url).then(r => r.json()).then(d => {
      if (mode === 'sat') {
        setItems((d.passes || [])
          .filter(p => new Date(p.start) > Date.now() && (p.visibility_tier||0) > 0)
          .sort((a,b) => (b.visibility_tier||0)-(a.visibility_tier||0) || new Date(a.start)-new Date(b.start)));
      } else if (mode === 'planet') {
        setItems((d.bodies || []).filter(b => b.type === 'planet' && b.above_horizon)
          .sort((a,b) => b.elevation_deg - a.elevation_deg));
        setConditions(d.bodies?.find(b => b.type === 'moon'));
      } else {
        setItems(d.targets || []);
        setConditions(d.conditions);
      }
      setLoading(false);
    }).catch(() => { setError('Could not load'); setLoading(false); });
  }, [open, mode, observer?.lat, observer?.lon]);

  const noLocation = !observer;

  function SatRow({ p }) {
    const tier = p.visibility_tier || 0, color = passTierColor(tier), key = `${p.norad_id}_${p.start}`;
    const isOpen = expanded === key;
    return (
      <div style={{ borderBottom:'1px solid var(--hair)', borderLeft:`2px solid ${color}` }}>
        <div style={{ padding:'11px 16px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5 }}>
            <span style={{ fontSize: 'var(--ui-body)', color:'var(--txt-bright)', fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:175 }}>{p.name}</span>
            <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize: 'var(--ui-small)', letterSpacing:'0.08em', padding:'2px 7px', borderRadius:99, background:'var(--bg-2)', color, border:`1px solid ${color}` }}>
              <i className={`ti ${TIER_ICON[tier]}`} style={{ fontSize: 'var(--ui-label)' }} />{TIER_LABEL[tier]}
            </span>
          </div>
          <div style={{ display:'flex', gap:14, fontSize: 'var(--ui-label)', color:'var(--txt-dim)', flexWrap:'wrap', marginBottom:8 }}>
            <span><span style={{ color:'var(--txt-bright)' }}>{fmtTime(p.start)}</span> {countdown(p.start)}</span>
            <span>max <span style={{ color:'var(--txt)' }}>{p.max_el?.toFixed(0)}&deg;</span> {compass(p.azimuth_max)}</span>
            <span>{Math.round((p.duration_sec||0)/60)}m{(p.duration_sec||0)%60}s</span>
            {p.approx_magnitude!=null && <span>mag <span style={{ color:'var(--txt)' }}>{p.approx_magnitude>0?'+':''}{p.approx_magnitude}</span></span>}
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={() => setExpanded(isOpen ? null : key)} style={btnPrimary(isOpen)}>
              <i className="ti ti-sparkles" aria-hidden="true" style={{ fontSize: 'var(--ui-label)' }} />{isOpen ? 'Hide briefing' : 'Briefing'}
            </button>
            <button onClick={() => onGoto?.({ norad_1: p.norad_id, title: p.name })} title="Track" style={btnGhost()}>
              <i className="ti ti-crosshair" style={{ fontSize: 'var(--ui-label)' }} />Track
            </button>
          </div>
          {isOpen && <Briefing pass={p} experience={experience} />}
        </div>
      </div>
    );
  }

  function StaticRow({ o, isPlanet }) {
    const score = isPlanet ? null : o.score;
    const color = isPlanet ? 'var(--cyan)' : scoreColor(score);
    const alt = isPlanet ? o.elevation_deg : o.altitude_deg;
    const az  = isPlanet ? o.azimuth_deg   : o.azimuth_deg;
    const label = isPlanet ? o.name : (o.name || o.id);
    const sub   = isPlanet ? 'planet' : `${o.id} · ${o.type}`;
    return (
      <div style={{ padding:'11px 16px', borderBottom:'1px solid var(--hair)', borderLeft:`2px solid ${color}` }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
          <span style={{ fontSize: 'var(--ui-body)', color:'var(--txt-bright)', fontWeight:500 }}>{label}</span>
          {!isPlanet && <span style={{ fontSize: 'var(--ui-small)', letterSpacing:'0.08em', padding:'2px 7px', borderRadius:99, background:'var(--bg-2)', color, border:`1px solid ${color}` }}>score {score}</span>}
        </div>
        <div style={{ fontSize: 'var(--ui-label)', color:'var(--txt-mute)', marginBottom:6 }}>{sub}{o.con ? ` · ${o.con}` : ''}</div>
        <div style={{ display:'flex', gap:14, fontSize: 'var(--ui-label)', color:'var(--txt-dim)', flexWrap:'wrap' }}>
          <span>alt <span style={{ color:'var(--txt)' }}>{alt?.toFixed(0)}&deg;</span> {compass(az)}</span>
          {o.mag!=null && <span>mag <span style={{ color:'var(--txt)' }}>{o.mag>0?'+':''}{o.mag}</span></span>}
          {o.size_arcmin && <span>{o.size_arcmin}&#39;</span>}
          {o.moon_sep_deg!=null && <span>moon {o.moon_sep_deg}&deg;</span>}
        </div>
      </div>
    );
  }

  const content = (
    <div style={{ fontFamily:'var(--mono)', color:'var(--txt)' }}>
      {noLocation && (
        <div style={{ padding:'28px 18px', color:'var(--txt-dim)', fontSize: 'var(--ui-label)', textAlign:'center', lineHeight:1.6 }}>
          <i className="ti ti-map-pin-off" style={{ fontSize:20, display:'block', marginBottom:8, color:'var(--txt-mute)' }} />
          Set your location to see what&#39;s overhead tonight.
        </div>
      )}
      {!noLocation && conditions && (mode === 'deepsky') && (
        <div style={{ padding:'7px 16px', borderBottom:'1px solid var(--hair)', fontSize: 'var(--ui-label)', letterSpacing:'0.04em', color:'var(--txt-mute)' }}>
          {conditions.darkness} sky &middot; moon {Math.round((conditions.moon_phase_fraction||0)*100)}%{conditions.moon_up ? ` up ${conditions.moon_alt_deg?.toFixed(0)}\u00b0` : ' down'}
        </div>
      )}
      {!noLocation && loading && <div style={{ padding:'28px 18px', color:'var(--txt-dim)', fontSize: 'var(--ui-label)', textAlign:'center' }}>Computing&hellip;</div>}
      {!noLocation && error && <div style={{ padding:'18px', color:'var(--rose)', fontSize: 'var(--ui-label)' }}>{error}</div>}
      {!noLocation && !loading && !error && items.length === 0 && (
        <div style={{ padding:'28px 18px', color:'var(--txt-dim)', fontSize: 'var(--ui-label)', textAlign:'center', lineHeight:1.6 }}>
          {mode === 'sat' ? `No visible passes in the next ${PASS_WINDOW_HOURS} hours.` : mode === 'planet' ? 'No planets above the horizon now.' : 'No deep-sky targets above 10° now.'}
        </div>
      )}
      {!loading && items.map((it, i) =>
        mode === 'sat'    ? <SatRow key={i} p={it} /> :
        mode === 'planet' ? <StaticRow key={i} o={it} isPlanet /> :
                            <StaticRow key={i} o={it} isPlanet={false} />
      )}
    </div>
  );

  const modeBar = !noLocation && (
    <div style={{ display:'flex', padding:'8px 16px 0', gap:4, flexShrink:0 }}>
      {MODES.map(([val, label]) => (
        <button key={val} onClick={() => setMode(val)} style={{
          flex:1, padding:'6px 0', fontFamily:'var(--mono)', fontSize: 'var(--ui-label)', letterSpacing:'0.04em',
          border:'1px solid', borderColor: mode===val ? 'var(--accent)' : 'var(--hair-2)',
          borderBottom: mode===val ? '2px solid var(--accent)' : '1px solid var(--hair-2)',
          borderRadius:'4px 4px 0 0', cursor:'pointer',
          background: mode===val ? 'var(--bg-2)' : 'transparent',
          color: mode===val ? 'var(--txt-bright)' : 'var(--txt-dim)',
        }}>{label}</button>
      ))}
    </div>
  );

  const expBar = !noLocation && mode === 'sat' && items.length > 0 && (
    <div style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 16px', borderBottom:'1px solid var(--hair)', flexShrink:0 }}>
      <span style={{ fontSize: 'var(--ui-label)', letterSpacing:'0.06em', color:'var(--txt-mute)' }}>BRIEFING FOR</span>
      <div style={{ display:'inline-flex', border:'1px solid var(--hair-2)', borderRadius:999, overflow:'hidden' }}>
        {EXPERIENCE.map(([v,l]) => (
          <button key={v} onClick={() => setExperience(v)} style={{ padding:'3px 11px', fontFamily:'var(--mono)', fontSize: 'var(--ui-label)', border:'none', cursor:'pointer', background: experience===v ? 'var(--accent)' : 'transparent', color: experience===v ? '#0e0e0e' : 'var(--txt-dim)' }}>{l}</button>
        ))}
      </div>
    </div>
  );

  if (isMobile) {
    return <BottomSheet open={open} onClose={onClose} title="Tonight's sky" maxHeight="80vh"><VerdictHeader observer={observer} activity={activity} onActivity={handleActivity} />{modeBar}{expBar}{content}</BottomSheet>;
  }
  if (!open) return null;
  return (
    <PopoutModal title="Tonight's sky" onClose={onClose}>
      <VerdictHeader observer={observer} activity={activity} onActivity={handleActivity} />
      {modeBar}{expBar}
      <div style={{ overflowY:'auto', flex:1 }}>{content}</div>
    </PopoutModal>
  );
}

function btnPrimary(on) {
  return { flex:1, display:'inline-flex', alignItems:'center', justifyContent:'center', gap:5, padding:'5px 0', fontFamily:'var(--mono)', fontSize: 'var(--ui-label)', letterSpacing:'0.06em', background: on ? 'var(--accent)' : 'transparent', color: on ? '#0e0e0e' : 'var(--accent)', border:'1px solid var(--accent)', borderRadius:4, cursor:'pointer' };
}
function btnGhost() {
  return { display:'inline-flex', alignItems:'center', gap:5, padding:'5px 12px', fontFamily:'var(--mono)', fontSize: 'var(--ui-label)', letterSpacing:'0.06em', background:'transparent', color:'var(--txt)', border:'1px solid var(--hair-2)', borderRadius:4, cursor:'pointer' };
}
